import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    Subject,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    takeUntil,
} from 'rxjs';
import {
    ANY_SENIORITY_CODE,
    ResourcesStepResult,
    WizardPreferred,
    WizardRequestedResource,
} from '../../../../models/request-wizard.models';
import { ResourceDto } from '../../../../models/resource.models';
import { ResourceService } from '../../../../services/resource.service';
import { formatResourceSubtitle } from '../../../../utils/resource-subtitle.utils';
import { RequestedResourcesEditorComponent } from '../../../shared/requested-resources-editor/requested-resources-editor.component';

/** Number of resource results to fetch for the preferred resources typeahead. */
const PREFERRED_SEARCH_PAGE_SIZE = 25;

/** A search result paired with its precomputed "Email • Title • Seniority" subtitle. */
interface PreferredSuggestion {
  resource: ResourceDto;
  subtitle: string;
}

/**
 * Resources wizard step. Combines two related concerns:
 *  - Requested Resources: how many people of each seniority are needed.
 *  - Preferred Resources: a typeahead of people the requestor would prefer.
 *
 * Per-matter assignments live in the separate Assignments step.
 */
@Component({
  selector: 'app-resources-step',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RequestedResourcesEditorComponent,
  ],
  templateUrl: './resources-step.component.html',
})
export class ResourcesStepComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private resourceService = inject(ResourceService);

  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialResult: ResourcesStepResult | null = null;

  @Output() resultChange = new EventEmitter<ResourcesStepResult>();

  form!: FormGroup;

  // Requested resources (count per seniority)
  requestedResources: WizardRequestedResource[] = [
    { count: 1, seniorityCode: ANY_SENIORITY_CODE },
  ];

  // Preferred resources
  preferred: WizardPreferred[] = [];
  preferredSuggestions: PreferredSuggestion[] = [];
  isLoadingPreferred = false;
  preferredNotFound = false;

  private destroy$ = new Subject<void>();
  private preferredQuery$ = new Subject<string>();

  ngOnInit(): void {
    this.form = this.fb.group({ preferredSearch: [''] });

    if (this.initialResult) {
      if ((this.initialResult.requestedResources ?? []).length > 0) {
        this.requestedResources = [...this.initialResult.requestedResources];
      }
      this.preferred = [...(this.initialResult.preferred ?? [])];
    }

    this.setupPreferredTypeahead();
    this.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== Requested resources =====

  /** Total number of resources requested across all seniority rows. */
  get requestedTotal(): number {
    return this.requestedResources.reduce((sum, r) => sum + (r.count || 0), 0);
  }

  /** Receives the updated rows from the shared requested-resources editor. */
  onRequestedRowsChange(rows: WizardRequestedResource[]): void {
    this.requestedResources = rows;
    this.form.markAsDirty();
    this.emit();
  }

  // ===== Preferred resources =====

  private setupPreferredTypeahead(): void {
    this.preferredQuery$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        switchMap((query) => {
          if (!query || query.trim().length < 3) {
            this.isLoadingPreferred = false;
            this.preferredSuggestions = [];
            this.preferredNotFound = false;
            return [];
          }
          this.isLoadingPreferred = true;
          return this.resourceService.searchAllResources({
            page: 1,
            pageSize: PREFERRED_SEARCH_PAGE_SIZE,
            search: query,
            sortBy: 'name',
            sortDirection: 'asc',
          });
        }),
      )
      .subscribe((result) => {
        this.isLoadingPreferred = false;
        if (result && result.items) {
          const addedIds = this.preferred.map((r) => r.id);
          this.preferredSuggestions = result.items
            .filter((r) => !addedIds.includes(r.employeeId))
            .map((r) => ({
              resource: r,
              subtitle: formatResourceSubtitle(r.email, r.title, r.seniority),
            }));
          this.preferredNotFound = this.preferredSuggestions.length === 0;
        }
      });
  }

  onPreferredInput(event: Event): void {
    this.preferredQuery$.next((event.target as HTMLInputElement).value);
  }

  onPreferredBlur(): void {
    setTimeout(() => (this.preferredSuggestions = []), 200);
  }

  addPreferred(resource: ResourceDto): void {
    if (this.preferred.some((p) => p.id === resource.employeeId)) return;
    this.preferred = [
      ...this.preferred,
      {
        id: resource.employeeId,
        name: resource.fullName,
        role: resource.title || 'Unknown',
        email: resource.email,
        seniorityCode: resource.seniorityCode,
        seniority: resource.seniority,
      },
    ];
    this.preferredSuggestions = [];
    this.form.patchValue({ preferredSearch: '' }, { emitEvent: false });
    this.preferredQuery$.next('');
    this.form.markAsDirty();
    this.emit();
  }

  removePreferred(id: string): void {
    this.preferred = this.preferred.filter((p) => p.id !== id);
    this.form.markAsDirty();
    this.emit();
  }

  private emit(): void {
    this.resultChange.emit({
      requestedResources: this.requestedResources,
      preferred: this.preferred,
    });
  }
}
